import { useState, useEffect, useCallback, useRef } from "react";
import { scoutRequestService } from "../../services/scoutRequestService";

// Club "our scout requests" list (design 1b). Counts come inline from the
// club-only serializer. The category segmented filter (All/Professional/Youth) is
// applied client-side — the list endpoint carries every category and paginates by
// status only, so a page filter would fight pagination. Open/closed IS a server
// filter (?status=). Mirrors the AbortController + refetch shape of
// useClubNetworkFeed.
export const useOurScoutRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState(""); // "" | professional | youth
  const [statusFilter, setStatusFilter] = useState(""); // "" | open | closed
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
  });
  const abortRef = useRef(null);

  const fetchRequests = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const params = new URLSearchParams();
      params.append("page", page);
      if (statusFilter) params.append("status", statusFilter);
      try {
        const res = await scoutRequestService.getRequests(params, controller.signal);
        if (res.success) {
          setRequests(res.data);
          setPagination(res.pagination);
        } else {
          setError(res.error || "Failed to load your scout requests.");
        }
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setError("Failed to load your scout requests. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [statusFilter],
  );

  useEffect(() => {
    fetchRequests(1);
  }, [fetchRequests]);

  const changeCategory = (c) => setCategory((prev) => (prev === c ? "" : c));

  // Client-side category slice (see note above).
  const visibleRequests = category
    ? requests.filter((r) => r.category === category)
    : requests;

  const closeRequest = useCallback(async (id, keepLongTerm, targetListId = null) => {
    const res = await scoutRequestService.closeRequest(id, keepLongTerm, targetListId);
    if (res.success) {
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, ...res.data } : r)));
    }
    return res;
  }, []);

  return {
    requests: visibleRequests,
    allRequests: requests,
    loading,
    error,
    category,
    changeCategory,
    statusFilter,
    setStatusFilter,
    pagination,
    fetchRequests,
    closeRequest,
  };
};

export default useOurScoutRequests;
